import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { getLoggedInUser } from "authentication/AuthenticationService";

import {
	asTitle,
	deleteElement,
	isEmailValid,
	replaceElement
} from "utils/Utils";

import { Right, UserRights } from "core/blueprint/base/BlueprintService";
import UserAccessRow from "core/blueprint/base/access/UserAccessRow";
import UserAccessPlaceholderRow from "core/blueprint/base/access/UserAccessPlaceholderRow";

export type UserAccessTableProps = {
	accesses: UserRights[];

	onChange: (accesses: UserRights[]) => void;
	isValid: (valid: boolean) => void;
};

const UserRightsTable: React.FC<UserAccessTableProps> = (
	props: UserAccessTableProps
) => {
	const { t } = useTranslation();
	const users = props.accesses.map(a => a.user);
	const rights = props.accesses.map(a => a.rights);
	const loggedInUserIndex = users.findIndex(u => u === getLoggedInUser());

	useEffect(() => {
		props.isValid(usersAreValid());
	}, [props.accesses]);

	function updateUser(index: number, email: string) {
		if (email.trim().length === 0) {
			props.onChange(deleteElement(index, props.accesses));
		} else {
			const newValue = {
				...props.accesses[index],
				user: email
			};
			props.onChange(replaceElement(index, newValue, props.accesses));
		}
	}

	function updateRights(index: number, rights: Right[]) {
		const newValue = {
			...props.accesses[index],
			rights: rights
		};
		props.onChange(replaceElement(index, newValue, props.accesses));
	}

	function addAccess(user: string) {
		const newRights = {
			user: user,
			rights: []
		};
		props.onChange(props.accesses.concat(newRights));
	}

	// All emails must be valid and unique!
	// There must be at least one admin!
	function usersAreValid(): boolean {
		return (
			users.every(u => isEmailValid(u)) &&
			new Set(users).size === users.length &&
			rights.some(p => p.includes(Right.ADMIN)) &&
			rights.every(p => p.length > 0)
		);
	}

	function userIsInvalid(user: string): boolean {
		return !(
			isEmailValid(user) &&
			props.accesses.filter(a => a.user === user).length === 1
		);
	}

	return (
		<Table>
			<TableHead>
				<TableRow>
					<TableCell>{asTitle(t("user"))}</TableCell>
					<TableCell>{asTitle(t("right_other"))}</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{props.accesses.map((a, index) => (
					<UserAccessRow
						key={`${index}/${props.accesses.length}`}
						user={a.user}
						rights={a.rights}
						disabled={index === loggedInUserIndex}
						error={userIsInvalid(a.user)}
						onUpdateUser={value => updateUser(index, value)}
						onUpdateRights={value => updateRights(index, value)}
					/>
				))}
				<UserAccessPlaceholderRow
					key={props.accesses.length}
					users={props.accesses.map(a => a.user)}
					onInsert={addAccess}
				/>
			</TableBody>
		</Table>
	);
};

export default UserRightsTable;
